'use client';

import { useState, useEffect, useMemo, type Dispatch, type SetStateAction } from 'react';
import { ChevronLeft, ChevronRight, Loader2, AlertCircle } from 'lucide-react';
import { priceForDate, slotPriceForDate } from '@/lib/excursion-seasonal-slots';
import {
  buildCalendarDays,
  toLocalDateStr,
  MONTH_NAMES,
  WEEKDAY_LABELS,
} from '@/components/ui/date-range-picker/calendarUtils';
import type { ExcursionAsset } from '@/components/booking/UnifiedBookingModal';
import type { ExcursionWizardState } from '@/components/booking/excursion/ExcursionWizard';

/* ── Types ──────────────────────────────────────────────────────────────────── */

interface Departure {
  start_time: string;       // 'HH:MM' — ora partenza (local time)
  capacity: number | null;
  booked_count: number;
  remaining: number | null; // null = illimitato
  slot_id?: string;
}

interface AvailableDay {
  date: string;             // 'YYYY-MM-DD'
  departures: Departure[];
}

/* ── Props ──────────────────────────────────────────────────────────────────── */

interface StepWhenProps {
  asset: ExcursionAsset;
  state: ExcursionWizardState;
  setState: Dispatch<SetStateAction<ExcursionWizardState>>;
}

/* ── Helpers ────────────────────────────────────────────────────────────────── */

/** Estrae 'HH:MM' da una data ISO in timezone locale del browser. */
function isoToLocalTime(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/* ── Component ──────────────────────────────────────────────────────────────── */

export default function StepWhen({ asset, state, setState }: StepWhenProps) {
  const isDerived = asset.availabilityMode === 'derived';
  const markupRate = asset.markupRate ?? 0;
  const todayStr = useMemo(() => toLocalDateStr(new Date()), []);

  /* ── DERIVED: fetch available dates ──────────────────────────────────────── */
  const [loading, setLoading] = useState(isDerived);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [derivedDays, setDerivedDays] = useState<AvailableDay[]>([]);

  useEffect(() => {
    if (!isDerived) return;
    let alive = true;
    setLoading(true);
    setFetchError(null);
    fetch(`/api/excursions/${asset.id}/available-dates`, { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((j) => {
        if (!alive) return;
        if (j?.success) {
          setDerivedDays((j.data ?? []) as AvailableDay[]);
        } else {
          setFetchError('Date non disponibili al momento.');
        }
      })
      .catch(() => {
        if (alive) setFetchError('Errore nel caricamento delle disponibilità.');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => { alive = false; };
  }, [isDerived, asset.id]);

  /* ── MATERIALIZZATO: raggruppa slot per giorno ───────────────────────────── */
  const materializedDays = useMemo((): AvailableDay[] => {
    if (isDerived) return [];
    const map = new Map<string, Departure[]>();
    for (const slot of asset.slots) {
      const date = slot.start_at.slice(0, 10);
      if (date < todayStr) continue;
      const start_time = isoToLocalTime(slot.start_at);
      const remaining =
        slot.capacity != null
          ? Math.max(slot.capacity - slot.booked_count, 0)
          : null;
      const dep: Departure = {
        start_time,
        capacity: slot.capacity,
        booked_count: slot.booked_count,
        remaining,
        slot_id: slot.id,
      };
      const arr = map.get(date);
      if (arr) arr.push(dep);
      else map.set(date, [dep]);
    }
    return Array.from(map.entries())
      .filter(([, deps]) => deps.some((d) => d.remaining === null || d.remaining > 0))
      .map(([date, departures]) => ({
        date,
        departures: departures.sort((a, b) => a.start_time.localeCompare(b.start_time)),
      }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [isDerived, asset.slots, todayStr]);

  const availableDays = isDerived ? derivedDays : materializedDays;

  const availableDateSet = useMemo(
    () => new Set(availableDays.map((d) => d.date)),
    [availableDays],
  );

  /* ── Calendario: navigazione mese ───────────────────────────────────────── */
  const [currentMonth, setCurrentMonth] = useState<{ year: number; month: number }>(() => {
    // Materializzato: parti dal mese del primo slot futuro (già ordinato asc)
    if (asset.availabilityMode !== 'derived' && asset.slots.length > 0) {
      const d = new Date(asset.slots[0].start_at);
      return { year: d.getFullYear(), month: d.getMonth() };
    }
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });

  // Derived: quando arrivano i dati, salta al mese della prima data disponibile
  const firstAvailDate = availableDays[0]?.date;
  useEffect(() => {
    if (!firstAvailDate || state.date) return;
    const d = new Date(firstAvailDate + 'T12:00:00');
    setCurrentMonth({ year: d.getFullYear(), month: d.getMonth() });
  }, [firstAvailDate, state.date]);

  const calendarDays = useMemo(
    () => buildCalendarDays(currentMonth.year, currentMonth.month),
    [currentMonth],
  );

  const prevMonth = () =>
    setCurrentMonth((p) =>
      p.month === 0 ? { year: p.year - 1, month: 11 } : { year: p.year, month: p.month - 1 },
    );
  const nextMonth = () =>
    setCurrentMonth((p) =>
      p.month === 11 ? { year: p.year + 1, month: 0 } : { year: p.year, month: p.month + 1 },
    );

  /* ── Partenze del giorno selezionato ────────────────────────────────────── */
  const selectedDayDeps = useMemo(
    () => availableDays.find((d) => d.date === state.date)?.departures ?? [],
    [availableDays, state.date],
  );

  /* ── Prezzo per partenza ─────────────────────────────────────────────────── */
  const toClientPrice = (vendorPrice: number) =>
    Math.round(vendorPrice * (1 + markupRate / 100));

  const getDeparturePrice = (date: string, slotId?: string, startTime?: string): string => {
    const pm = asset.pricingMode;

    // Tiered: mostra "da €X" dalla fascia base (min non-exclusive) × markup
    if (pm === 'tiered' && asset.pricingTiers?.length) {
      const nonExclusive = asset.pricingTiers.filter((t) => !t.exclusive);
      if (nonExclusive.length > 0) {
        const minVendor = Math.min(...nonExclusive.map((t) => Number(t.price)));
        return `da €${toClientPrice(minVendor)}`;
      }
    }

    // Exclusive: tariffa flat gruppo × markup
    if (pm === 'exclusive' && asset.exclusivePrice) {
      return `€${toClientPrice(asset.exclusivePrice)} /gruppo`;
    }

    // Price override sullo slot (prevale sulla stagione)
    if (slotId) {
      const slot = asset.slots.find((s) => s.id === slotId);
      if (slot?.price_override != null && slot.price_override > 0) {
        const clientPrice = toClientPrice(slot.price_override);
        return pm === 'per_group'
          ? `€${clientPrice} /gruppo`
          : `€${clientPrice} /pers`;
      }
    }

    // Prezzo per FASCIA (granularità) → stagionale → fisso. Risolto per modalità
    // (per_person/per_group → campo dedicato, fallback price legacy).
    const md = date.slice(5, 10); // 'MM-DD'
    const fasciaPrice = startTime && asset.seasonalPeriods?.length
      ? slotPriceForDate(asset.seasonalPeriods, md, startTime, pm as 'per_person' | 'per_group' | undefined)
      : null;
    const vendorPrice =
      fasciaPrice ??
      (asset.seasonalPeriods?.length
        ? priceForDate(asset.seasonalPeriods, md)
        : null) ?? asset.price;

    const clientPrice = toClientPrice(vendorPrice);
    return pm === 'per_group' ? `€${clientPrice} /gruppo` : `€${clientPrice} /pers`;
  };

  /* ── Handlers ────────────────────────────────────────────────────────────── */
  const handleDayClick = (date: string) => {
    if (!availableDateSet.has(date)) return;
    setState((s) => ({ ...s, date, departureTime: null, slotId: null, slotRemaining: null }));
  };

  const handleDepartureSelect = (dep: Departure) => {
    const rem = dep.remaining;
    if (rem !== null && rem <= 0) return;
    setState((s) => ({
      ...s,
      departureTime: dep.start_time,
      slotId: dep.slot_id ?? null,
      slotRemaining: rem,
    }));
  };

  /* ── Render: loading / errore ────────────────────────────────────────────── */
  if (loading) {
    return (
      <div
        data-cid="step-when"
        className="flex flex-col items-center justify-center py-12 gap-3 text-slate-400"
      >
        <Loader2 size={24} className="animate-spin text-sky-500" />
        <p className="text-sm font-medium">Caricamento disponibilità…</p>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div
        data-cid="step-when"
        className="flex flex-col items-center justify-center py-12 gap-3 text-slate-500"
      >
        <AlertCircle size={24} className="text-red-400" />
        <p className="text-sm font-medium">{fetchError}</p>
      </div>
    );
  }

  /* ── Render: calendario + partenze ──────────────────────────────────────── */
  return (
    <div data-cid="step-when" className="flex flex-col gap-5 py-1">
      {/* ── Calendario mensile ──────────────────────────────────────────────── */}
      <div data-cid="step-when-calendar" className="select-none">
        {/* Navigazione mese */}
        <div className="flex items-center justify-between mb-3">
          <button
            type="button"
            onClick={prevMonth}
            aria-label="Mese precedente"
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-600 transition-colors"
          >
            <ChevronLeft size={17} />
          </button>
          <span className="text-sm font-bold text-slate-800 capitalize">
            {MONTH_NAMES[currentMonth.month]} {currentMonth.year}
          </span>
          <button
            type="button"
            onClick={nextMonth}
            aria-label="Mese successivo"
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-600 transition-colors"
          >
            <ChevronRight size={17} />
          </button>
        </div>

        {/* Intestazione giorni settimana */}
        <div className="grid grid-cols-7 mb-1">
          {WEEKDAY_LABELS.map((label) => (
            <div
              key={label}
              className="text-center text-[10px] font-bold text-slate-400 uppercase tracking-wider py-1"
            >
              {label}
            </div>
          ))}
        </div>

        {/* Griglia giorni */}
        <div className="grid grid-cols-7 gap-0.5">
          {calendarDays.map(({ date, day, isCurrentMonth }) => {
            if (!isCurrentMonth) {
              return <div key={date} className="min-h-[46px]" aria-hidden="true" />;
            }

            const isPast = date < todayStr;
            const isAvailable = !isPast && availableDateSet.has(date);
            const isSelected = date === state.date;
            const isToday = date === todayStr;

            let cellClass =
              'min-h-[46px] flex items-center justify-center rounded-lg text-sm font-semibold transition-all ';

            if (isSelected) {
              cellClass +=
                'bg-sky-600 text-white ring-2 ring-sky-300 ring-offset-1 cursor-pointer';
            } else if (isAvailable) {
              cellClass +=
                'bg-sky-50 text-sky-800 border border-sky-200 hover:bg-sky-100 cursor-pointer';
              if (isToday) cellClass += ' ring-1 ring-sky-400';
            } else {
              cellClass += 'text-slate-300 cursor-default';
            }

            return (
              <button
                key={date}
                type="button"
                data-cid={`step-when-day-${date}`}
                disabled={!isAvailable}
                onClick={() => handleDayClick(date)}
                aria-pressed={isSelected}
                aria-label={`${day} ${MONTH_NAMES[currentMonth.month]}${isAvailable ? ', disponibile' : ''}`}
                className={cellClass}
              >
                <span className={isToday && !isSelected ? 'underline underline-offset-2' : ''}>
                  {day}
                </span>
              </button>
            );
          })}
        </div>

        {/* Messaggio nessuna data disponibile */}
        {availableDays.length === 0 && (
          <p className="text-sm text-center text-slate-400 mt-5 py-2">
            Nessuna data disponibile nel periodo.
          </p>
        )}
      </div>

      {/* ── Partenze del giorno selezionato ──────────────────────────────────── */}
      {state.date && selectedDayDeps.length > 0 && (
        <div className="flex flex-col gap-2">
          <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
            Orari di partenza
          </p>
          <div className="flex flex-wrap gap-2">
            {selectedDayDeps.map((dep) => {
              const remaining = dep.remaining;
              const isDisabled = remaining !== null && remaining <= 0;
              const isSelected = state.departureTime === dep.start_time;
              const priceLabel = getDeparturePrice(state.date!, dep.slot_id, dep.start_time);

              return (
                <button
                  key={dep.start_time}
                  type="button"
                  data-cid={`step-when-departure-${dep.start_time.replace(':', '')}`}
                  disabled={isDisabled}
                  onClick={() => handleDepartureSelect(dep)}
                  aria-pressed={isSelected}
                  className={[
                    'flex flex-col items-start px-3 py-2 rounded-xl border text-left transition-all',
                    isSelected
                      ? 'bg-sky-600 border-sky-600 text-white shadow-sm'
                      : isDisabled
                      ? 'bg-slate-50 border-slate-200 text-slate-300 cursor-not-allowed'
                      : 'bg-white border-slate-200 text-slate-700 hover:border-sky-400 hover:bg-sky-50 cursor-pointer',
                  ].join(' ')}
                >
                  <span className="text-sm font-bold leading-tight">{dep.start_time}</span>
                  <span
                    className={`text-xs mt-0.5 leading-tight ${
                      isSelected ? 'text-white/80' : 'text-slate-400'
                    }`}
                  >
                    {remaining === null
                      ? 'posti disponibili'
                      : remaining > 0
                      ? `${remaining} posti`
                      : 'Esaurito'}
                    {' · '}
                    {priceLabel}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
